import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { NotFound } from "@/pages/public/StatusPages";
import { api } from "@/lib/api";
import { applyMeta, setPageMeta } from "@/lib/seo";
import { FUNDING_TYPES } from "@/lib/format";
import { DemoBadge, Empty } from "@/components/Badges";
import OpportunityView, { OppCardMeta } from "@/components/OpportunityView";
import { useAuth, startLogin } from "@/context/AuthContext";

export const OppFilters = ({ f, setF }) => (
  <div className="grid gap-3 sm:grid-cols-4">
    <input aria-label="Search funding" data-testid="funding-search-input" className="input sm:col-span-2" placeholder="Search by keyword or funder" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} />
    <select aria-label="Funding type" data-testid="funding-type-filter" className="input" value={f.funding_type} onChange={(e) => setF({ ...f, funding_type: e.target.value })}>
      <option value="">All funding types</option>
      {Object.entries(FUNDING_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
    <select aria-label="Status" data-testid="funding-status-filter" className="input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
      <option value="">Any status</option><option value="open">Open</option><option value="upcoming">Upcoming</option><option value="unconfirmed">Intake to confirm</option><option value="closed">Closed</option>
    </select>
  </div>
);

export default function PublicFunding() {
  const [f, setF] = useState({ q: "", funding_type: "", status: "open" });
  const [rows, setRows] = useState(null);
  const { user } = useAuth();
  useEffect(() => {
    const t = setTimeout(() => api.get("/public/opportunities", { params: f }).then(({ data }) => setRows(data)), 300);
    return () => clearTimeout(t);
  }, [f]);
  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
      <p className="caption text-[#0D9488]">Grants & funding</p>
      <h1 className="mt-3 text-4xl font-bold text-[#0B192C] sm:text-5xl">Funding directory</h1>
      <p className="mt-4 max-w-3xl text-slate-600">Opportunities from GOV.UK Find a Grant, refreshed daily, plus other official sources added by our team. Sign in to check eligibility against your business profile.</p>
      {!user && <button onClick={startLogin} data-testid="funding-signin-match-button" className="btn-teal mt-6">Sign in to see your matches</button>}
      <div className="mt-10"><OppFilters f={f} setF={setF} /></div>
      <p className="caption mt-6" data-testid="funding-result-count">{rows ? `${rows.length} opportunities` : "Loading…"}</p>
      <div className="mt-4 space-y-3">
        {rows?.length === 0 && <Empty title="No opportunities match these filters" />}
        {rows?.map((o) => (
          <Link key={o.id} to={`/grants-funding/${o.id}`} data-testid={`opportunity-card-${o.id}`} className="card block p-5 transition-colors hover:border-[#0D9488]">
            <div className="flex flex-wrap items-center gap-2">{o.is_demo && <DemoBadge />}<span className="caption">{o.funder}</span></div>
            <h2 className="mt-1 font-display font-semibold text-[#0B192C]">{o.title}</h2>
            <OppCardMeta o={o} />
          </Link>
        ))}
      </div>
    </div>
  );
}

export const PublicOpportunity = () => {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [missing, setMissing] = useState(false);
  const navigate = useNavigate();
  const { settings } = useAuth();
  useEffect(() => {
    setMissing(false);
    api.get(`/public/opportunities/${id}`).then(({ data }) => {
      if (data.id !== id) {
        navigate(`/grants-funding/${data.id}`, { replace: true });
        return;
      }
      setO(data);
      const meta = { title: data.title, desc: (data.summary_text || data.summary || "").slice(0, 155) || "Eligibility notes, deadlines and the official source for this UK funding opportunity.", index: true };
      setPageMeta(window.location.pathname, meta);
      applyMeta(meta, settings.platform_name, window.location.pathname);
    }).catch(() => setMissing(true));
  }, [id, settings.platform_name, navigate]);
  if (missing) return <NotFound />;
  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
      <Link to="/grants-funding" className="text-sm text-[#0D9488] hover:underline">← All funding</Link>
      <div className="mt-6">{o ? <OpportunityView o={o} actions={<button onClick={startLogin} data-testid="opp-signin-check-button" className="btn-teal">Check my eligibility</button>} /> : "Loading…"}</div>
    </div>
  );
};
